import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  ChevronDown,
} from "lucide-react";
import { BASE_URL } from "utils/constant";

const WorkerAttendance = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [attendanceById, setAttendanceById] = useState(() => ({}));
  const [remoteWorkers, setRemoteWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const normalizeDate = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const isSameDay = (a, b) =>
    normalizeDate(a).getTime() === normalizeDate(b).getTime();
  const formatDate = (d) => {
    const today = new Date();
    const isToday = isSameDay(d, today);
    const formatted = new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
    return isToday ? `Today, ${formatted}` : formatted;
  };
  const goPrevDay = () => {
    setCurrentDate(
      (prev) =>
        new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1)
    );
  };
  const goNextDay = () => {
    setCurrentDate((prev) => {
      const next = new Date(
        prev.getFullYear(),
        prev.getMonth(),
        prev.getDate() + 1
      );
      const today = new Date();
      return normalizeDate(next) > normalizeDate(today) ? today : next;
    });
  };
  const isToday = isSameDay(currentDate, new Date());

  useEffect(() => {
    if (!isOpen) return;

    const fetchWorkers = async () => {
      setLoadingWorkers(true);
      setError("");
      try {
        const site = localStorage.getItem("site");
        const accessToken = localStorage.getItem("access");
        const response = await fetch(`${BASE_URL}/worker/workers-page/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            worker_type: "Worker",
            attendance_status: "Unmarked",
            sites: parseInt(site),
          }),
        });
        const data = await response.json();
        const rawList =
          data?.status && Array.isArray(data?.data) ? data.data : [];
        const list = rawList.filter(
          (w) =>
            String(w?.attendance_status || "").toLowerCase() === "unmarked" &&
            String(w?.worker_type || "").toLowerCase() === "worker"
        );
        const normalized = list.map((w) => ({
          id: w.worker_id || w.id,
          name: w.worker_name || w.name,
          daily_wage: w.daily_wage || 800,
          raw: w,
        }));
        setRemoteWorkers(normalized);

        // Initialize defaults: status = "Absent"
        const defaults = {};
        normalized.forEach((w) => {
          defaults[w.id] = {
            status: "Present",
            dayType: "Full day",
            overtimeHrs: 0,
            overtimeWage: 0,
          };
        });
        setAttendanceById(defaults);
      } catch (e) {
        setError("Failed to load workers");
        setRemoteWorkers([]);
      } finally {
        setLoadingWorkers(false);
      }
    };

    fetchWorkers();
  }, [isOpen]);

  const filteredWorkers = useMemo(() => {
    if (!query.trim()) return remoteWorkers;
    const q = query.toLowerCase();
    return remoteWorkers.filter((w) =>
      String(w.name || "")
        .toLowerCase()
        .includes(q)
    );
  }, [remoteWorkers, query]);

  if (!isOpen) return null;

  const isAllSelected =
    filteredWorkers.length > 0 &&
    filteredWorkers.every((w) => selected.has(w.id));

  const toggleSelectAll = () => {
    const next = new Set(selected);
    if (isAllSelected) {
      filteredWorkers.forEach((w) => next.delete(w.id));
    } else {
      filteredWorkers.forEach((w) => next.add(w.id));
    }
    setSelected(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const updateAttendance = (id, field, value) => {
    setAttendanceById((prev) => {
      const current = prev[id] || {};
      const updated = { ...current, [field]: value };

      // Reset cascading fields when status changes
      if (field === "status") {
        if (value === "Absent") {
          updated.dayType = "Full day";
          updated.overtimeHrs = 0;
          updated.overtimeWage = 0;
        }
      }

      // Reset overtime when dayType changes away from Overtime
      if (field === "dayType" && value !== "Overtime") {
        updated.overtimeHrs = 0;
        updated.overtimeWage = 0;
      }

      // Auto-calculate overtime wage when hours change
      if (field === "overtimeHrs") {
        const worker = remoteWorkers.find((w) => w.id === id);
        const dailyWage = worker?.daily_wage || 800;
        const hrs = Math.min(10, Math.max(0, parseInt(value, 10) || 0));
        updated.overtimeHrs = hrs;
        updated.overtimeWage = Math.round((dailyWage / 8) * hrs);
      }

      return { ...prev, [id]: updated };
    });
  };

  const mapAttendanceToBody = (id) => {
    const attendance = attendanceById[id] || {};
    const { status, dayType, overtimeHrs, overtimeWage } = attendance;

    if (status === "Absent") {
      return {
        attendance_status: "Absent",
        half_day: "False",
        full_day: "False",
        overtime_hours: 0,
        overtime_wage: 0,
        persion_count: 1,
      };
    }

    // Status is "Present"
    if (dayType === "Half day") {
      return {
        attendance_status: "Present",
        half_day: "True",
        full_day: "False",
        overtime_hours: 0,
        overtime_wage: 0,
        persion_count: 1,
      };
    }

    if (dayType === "Full day") {
      return {
        attendance_status: "Present",
        half_day: "False",
        full_day: "True",
        overtime_hours: 0,
        overtime_wage: 0,
        persion_count: 1,
      };
    }

    if (dayType === "Overtime") {
      return {
        attendance_status: "Present",
        half_day: "False",
        full_day: "True",
        overtime_hours: overtimeHrs || 0,
        overtime_wage: overtimeWage || 0,
        persion_count: 1,
      };
    }

    return {
      attendance_status: "Present",
      half_day: "False",
      full_day: "True",
      overtime_hours: 0,
      overtime_wage: 0,
      persion_count: 1,
    };
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const accessToken = localStorage.getItem("access");
      const toSubmit = remoteWorkers.filter((w) => selected.has(w.id));
      await Promise.all(
        toSubmit.map((w) =>
          fetch(`${BASE_URL}/worker/mark-worker-attendance/${w.id}/`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(mapAttendanceToBody(w.id)),
          }).then((res) => {
            if (!res.ok) throw new Error("Failed to mark");
          })
        )
      );
      onClose && onClose();
    } catch (e) {
      setError("Failed to mark attendance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3">
      <div className="w-[60vw] rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="relative border-b px-6 pt-8 pb-5">
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-base font-semibold text-[#232323]">
            Workers Attendance
          </div>
          <button
            onClick={onClose}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="border-b px-6 py-3">
          {/* Row 1: Date control */}
          <div className="mb-2 flex items-center">
            <div className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm shadow-sm border border-gray-200">
              <button
                onClick={goPrevDay}
                className="mr-2 rounded p-1 hover:bg-gray-100 focus:outline-none"
                aria-label="Previous day"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span>{formatDate(currentDate)}</span>
              <button
                onClick={goNextDay}
                disabled={isToday}
                className="ml-2 rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Row 2: Select all + [total + search] */}
          <div className="flex items-center justify-between gap-3">
            <label className="flex select-none items-center gap-2 text-sm text-[#232323]">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-[#3B0069] focus:ring-[#3B0069]"
              />
              Select all
            </label>

            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                Total {remoteWorkers.length} workers
              </div>
              <div className="relative w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search in workers"
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#3B0069] focus:ring-1 focus:ring-[#3B0069]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="max-h-[55vh] overflow-auto px-5 py-2">
          {loadingWorkers ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Loading workers...
            </div>
          ) : (
            filteredWorkers.map((w) => {
              const attendance = attendanceById[w.id] || {};
              const isPresent = attendance.status === "Present";
              const isOvertime = attendance.dayType === "Overtime";

              return (
                <div
                  key={w.id}
                  className="flex items-center gap-3 border-b pb-3 pt-4  last:border-b-0"
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selected.has(w.id)}
                    onChange={() => toggleOne(w.id)}
                    className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#3B0069] focus:ring-[#3B0069]"
                  />

                  {/* Worker Name */}
                  <div className="w-48 flex-shrink-0 truncate text-sm font-medium text-gray-900">
                    {w.name}
                  </div>

                  {/* Status Select */}
                  <div className="relative w-32 flex-shrink-0">
                    <select
                      value={attendance.status || "Absent"}
                      onChange={(e) =>
                        updateAttendance(w.id, "status", e.target.value)
                      }
                      className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 pr-8 text-sm text-gray-800 focus:border-[#3B0069] focus:outline-none"
                    >
                      <option value="Absent">Absent</option>
                      <option value="Present">Present</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>

                  {/* Day Type Select (only if Present) */}
                  {isPresent && (
                    <div className="relative w-36 flex-shrink-0">
                      <select
                        value={attendance.dayType || "Full day"}
                        onChange={(e) =>
                          updateAttendance(w.id, "dayType", e.target.value)
                        }
                        className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 pr-8 text-sm text-gray-800 focus:border-[#3B0069] focus:outline-none"
                      >
                        <option value="Full day">Full day</option>
                        <option value="Half day">Half day</option>
                        <option value="Overtime">Overtime</option>
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  )}

                  {/* Overtime Hours & Wage (only if Present and Overtime) */}
                  {isPresent && isOvertime && (
                    <div className="flex flex-row gap-2 -mt-5">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-600 font-medium">
                          Overtime Hours
                        </label>
                        <div className="relative w-24">
                          <input
                            type="number"
                            // min={0}
                            max={10}
                            value={attendance.overtimeHrs || ""}
                            onChange={(e) =>
                              updateAttendance(
                                w.id,
                                "overtimeHrs",
                                e.target.value
                              )
                            }
                            placeholder="0"
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-3 py-2 pr-7 text-sm text-gray-700 focus:border-[#3B0069] focus:outline-none"
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                            hrs
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-600 font-medium">
                          Overtime Wage
                        </label>
                        <div className="relative w-32">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                            ₹
                          </span>
                          <input
                            type="number"
                            // min={0}
                            value={attendance.overtimeWage || ""}
                            onChange={(e) =>
                              updateAttendance(
                                w.id,
                                "overtimeWage",
                                e.target.value
                              )
                            }
                            placeholder="0"
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 pr-3 py-2 pl-7 text-sm text-gray-700 focus:border-[#3B0069] focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {filteredWorkers.length === 0 && (
            <div className="py-10 text-center text-sm text-gray-500">
              No workers found
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center px-6 py-3">
          <button
            onClick={handleSubmit}
            disabled={submitting || selected.size === 0}
            className={`w-64 rounded-lg px-4 py-2 text-white ${
              submitting || selected.size === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#3B0069] hover:bg-[#2d0050]"
            }`}
          >
            {submitting ? "Marking..." : "Mark Attendance"}
          </button>
        </div>

        {error && (
          <div className="px-6 pb-5 text-center text-sm text-red-600">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerAttendance;
